import { useState } from "react";
import { useTranslation } from "react-i18next";
// Constants
import { WEB_SERVICE, primaryColor, gray, borderColor, green, red } from "../../config/constants";
// Components
import {
  Body,
  Header,
  LogoImg,
  FormContainer,
  Prompt,
  PasswordContainer,
  Label,
  InputDataDiv,
  Input,
  RelativeButton,
  ConditionsContainer,
  ConditionText,
  ConditionAligner,
  ConditionImg,
  SaveButton,
  OkContainer,
  WrongContainer,
  CoachImg,
  OkContainerInfo,
  WrongContainerInfo,
  Title,
  Text,
  DeepLink,
} from "./styles";
import Loader from "../loader";
import UseFetch from "../../config/useFetch";
// Assets
import RGSLogo from "../../assets/rgs_plus_logo.png";
import CoachOk from "../../assets/coach_ok.png";
import CoachWrong from "../../assets/coach_wrong.png";
import VisiblePasswordImg from "../../assets/visible_password.svg";
import HidePasswordImg from "../../assets/hide_password.svg";
import CheckImg from "../../assets/check.svg";
import WrongImg from "../../assets/wrong.svg";

const UpdatePasswordUI = () => {
  // Password fields
  const [password, setPassword] = useState("");
  const [isVisiblePassword, setIsVisiblePassword] = useState(false);
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [isVisiblePasswordConfirm, setIsVisiblePasswordConfirm] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [hasFocusedThePasswordOnce, setHasFocusedThePasswordOnce] = useState(false);
  const [isPasswordConfirmFocused, setIsPasswordConfirmFocused] = useState(false);
  const [hasFocusedThePasswordConfirmOnce, setHasFocusedThePasswordConfirmOnce] = useState(false);
  // Conditions
  const [atLeast8Characters, setAtLeast8Characters] = useState(false);
  const [atLeast1UpperLetter, setAtLeast1UpperLetter] = useState(false);
  const [atLeast1LowerLetter, setAtLeast1LowerLetter] = useState(false);
  const [atLeast1Number, setAtLeast1Number] = useState(false);
  const [atLeast1SpecialCharacter, setAtLeast1SpecialCharacter] = useState(false);
  const [areThePasswordsTheSame, setAreThePasswordsTheSame] = useState(false);
  // Save data
  const [saveButtonClicked, setSaveButtonClicked] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isPasswordUpdated, setIsPasswordUpdated] = useState(false);
  const [token] = useState(window.location.pathname.split("/")[2]);

  const [t] = useTranslation("global");

  const isAllConditionsCorrect = () => {
    return atLeast8Characters && atLeast1UpperLetter && atLeast1LowerLetter && atLeast1Number && atLeast1SpecialCharacter;
  };

  const renderForm = () => {
    const renderCondition = (isFocused, condition, label) => {
      if (isFocused || (hasFocusedThePasswordOnce && !condition)) {
        return (
          <ConditionAligner>
            <ConditionImg src={condition ? CheckImg : WrongImg} alt="condition-icon" opacity={isFocused && !condition ? "0" : "1"} />
            <ConditionText color={isFocused && !condition ? gray : condition ? green : red}>{t(label)}</ConditionText>
          </ConditionAligner>
        );
      }
    };

    const renderPassword = () => {
      const setPasswordVisibility = (e) => {
        e.preventDefault();
        setIsVisiblePassword(!isVisiblePassword);
      };

      const handleSetPassword = (e) => {
        setPasswordConfirm("");
        setAreThePasswordsTheSame(false);
        const value = e.target.value;
        setPassword(value);
        setAtLeast8Characters(value.length >= 8);
        setAtLeast1UpperLetter(/\p{Lu}/u.test(value));
        setAtLeast1LowerLetter(/\p{Ll}/u.test(value));
        setAtLeast1Number(/\p{Nd}/u.test(value));
        setAtLeast1SpecialCharacter(/[!<>#$%&"'()*+,-./@?]/.test(value));
      };

      const handleSetOnFocusPassword = () => {
        setIsPasswordFocused(true);
        setHasFocusedThePasswordOnce(true);
      };

      const handleSetOnFocusOutPassword = () => {
        setIsPasswordFocused(false);
      };

      const getBorder = () => {
        const pixels = isPasswordFocused ? "2px" : "1px";
        const color = isAllConditionsCorrect()
          ? green
          : hasFocusedThePasswordOnce && !isPasswordFocused && !isAllConditionsCorrect()
          ? red
          : borderColor;
        return `${pixels} solid ${color}`;
      };

      return (
        <PasswordContainer>
          <Label>{t("password")}</Label>
          <InputDataDiv>
            <Input
              type={isVisiblePassword ? "text" : "password"}
              placeholder={t("passwordPlaceholder")}
              id="password"
              value={password}
              onChange={handleSetPassword}
              onFocus={handleSetOnFocusPassword}
              onBlur={handleSetOnFocusOutPassword}
              border={getBorder()}
            />
            <RelativeButton onClick={setPasswordVisibility}>
              <img src={isVisiblePassword ? HidePasswordImg : VisiblePasswordImg} alt="icon-password-button" />
            </RelativeButton>
          </InputDataDiv>
        </PasswordContainer>
      );
    };

    const renderConditions = () => {
      return (
        <ConditionsContainer>
          {isPasswordFocused && <ConditionText color={gray}>{t("criteria_title")}</ConditionText>}
          {renderCondition(isPasswordFocused, atLeast8Characters, "criteria_8Characters")}
          {renderCondition(isPasswordFocused, atLeast1UpperLetter, "criteria_upperLetter")}
          {renderCondition(isPasswordFocused, atLeast1LowerLetter, "criteria_lowerLetter")}
          {renderCondition(isPasswordFocused, atLeast1Number, "criteria_number")}
          {renderCondition(isPasswordFocused, atLeast1SpecialCharacter, "criteria_specialCharacter")}
        </ConditionsContainer>
      );
    };

    const renderPasswordConfirm = () => {
      const setPasswordConfirmVisibility = (e) => {
        e.preventDefault();
        setIsVisiblePasswordConfirm(!isVisiblePasswordConfirm);
      };

      const handleSetPassword = (e) => {
        const value = e.target.value;
        setPasswordConfirm(value);
        setAreThePasswordsTheSame(value === password);
      };

      const handleSetOnFocusPassword = () => {
        setIsPasswordConfirmFocused(true);
        setHasFocusedThePasswordConfirmOnce(true);
      };

      const handleSetOnFocusOutPassword = () => {
        setIsPasswordConfirmFocused(false);
      };

      const getBorder = () => {
        const pixels = isPasswordConfirmFocused ? "2px" : "1px";
        const color =
          passwordConfirm === password && passwordConfirm !== ""
            ? green
            : hasFocusedThePasswordConfirmOnce && !isPasswordConfirmFocused && (passwordConfirm !== password || passwordConfirm === "")
            ? red
            : borderColor;
        return `${pixels} solid ${color}`;
      };

      return (
        <PasswordContainer margin="32px 0 0">
          <Label>{t("passwordConfirm")}</Label>
          <InputDataDiv>
            <Input
              type={isVisiblePasswordConfirm ? "text" : "password"}
              placeholder={t("passwordConfirmPlaceholder")}
              id="passwordConfirm"
              value={passwordConfirm}
              onChange={handleSetPassword}
              onFocus={handleSetOnFocusPassword}
              onBlur={handleSetOnFocusOutPassword}
              border={getBorder()}
            />
            <RelativeButton onClick={setPasswordConfirmVisibility}>
              <img src={isVisiblePasswordConfirm ? HidePasswordImg : VisiblePasswordImg} alt="icon-password-button" />
            </RelativeButton>
          </InputDataDiv>
        </PasswordContainer>
      );
    };

    const renderPasswordCondition = () => {
      return (
        <ConditionsContainer>
          <ConditionAligner>
            <ConditionImg
              src={areThePasswordsTheSame && passwordConfirm ? CheckImg : WrongImg}
              alt="condition-icon"
              opacity={isPasswordConfirmFocused && (!areThePasswordsTheSame || !passwordConfirm) ? "0" : "1"}
            />
            <ConditionText
              color={
                isPasswordConfirmFocused && (!areThePasswordsTheSame || !passwordConfirm)
                  ? gray
                  : areThePasswordsTheSame && passwordConfirm
                  ? green
                  : red
              }
            >
              {t("passwordsMustBeTheSame")}
            </ConditionText>
          </ConditionAligner>
        </ConditionsContainer>
      );
    };

    const renderSaveButton = () => {
      const handleSavePassword = (e) => {
        e.preventDefault();
        setSaveButtonClicked(true);
        setIsSavingPassword(true);

        UseFetch("post", `${WEB_SERVICE}/patient/change-password`, new AbortController(), {
          token,
          password,
        })
          .then(({ data }) => {
            if (data.passwordUpdated) setIsPasswordUpdated(true);
            setIsSavingPassword(false);
          })
          .catch((error) => {
            console.log("Verify email: ", error);
            setIsSavingPassword(false);
          });
      };

      const isCorrect = isAllConditionsCorrect() && areThePasswordsTheSame;

      return (
        <SaveButton
          onClick={handleSavePassword}
          background={!isCorrect ? "#F2F2F2" : primaryColor}
          color={!isCorrect ? gray : "#FFF"}
          disabled={!isCorrect}
        >
          {t("savePassword")}
        </SaveButton>
      );
    };

    return (
      <FormContainer>
        <Prompt>{t("securePasswordPrompt")}</Prompt>
        {renderPassword()}
        {(isPasswordFocused || (hasFocusedThePasswordOnce && !isAllConditionsCorrect())) && renderConditions()}
        {renderPasswordConfirm()}
        {(isPasswordConfirmFocused || (hasFocusedThePasswordConfirmOnce && (passwordConfirm !== password || !passwordConfirm))) &&
          renderPasswordCondition()}
        {renderSaveButton()}
      </FormContainer>
    );
  };

  const getDeviceType = () => {
    const ua = navigator.userAgent || window.opera;
    const isAndroid = /android/i.test(ua);
    const isIOS = /iPhone|iPad|Macintosh|iPod/i.test(ua);
    return isAndroid || isIOS;
  };

  const renderCoachOK = () => {
    return (
      <OkContainer>
        <CoachImg src={CoachOk} alt="coach-ok" />
        <OkContainerInfo>
          <Title>{t("passwordUpdatedTitle")}</Title>
          <Text>{t("passwordUpdatedPrompt")}.</Text>
          {getDeviceType() && <DeepLink href="unitydl://mylink?rgs_password_changed">{t("goBackToRGS")}</DeepLink>}
        </OkContainerInfo>
      </OkContainer>
    );
  };

  const renderCoachWrong = () => {
    return (
      <WrongContainer>
        <WrongContainerInfo>
          <Title>{t("expiredLinkTitle")}</Title>
          <Text>{t("expiredLinkPrompt")}.</Text>
        </WrongContainerInfo>
        <CoachImg src={CoachWrong} alt="coach-wrong" />
      </WrongContainer>
    );
  };

  return (
    <Body>
      <Header marginBottom={isPasswordUpdated ? "16px" : "32px"}>
        <LogoImg src={RGSLogo} alt="logo" />
      </Header>
      {!saveButtonClicked ? renderForm() : isSavingPassword ? <Loader /> : isPasswordUpdated ? renderCoachOK() : renderCoachWrong()}
    </Body>
  );
};

export default UpdatePasswordUI;
